import {
  mdiCheckCircle,
  mdiClockAlertOutline,
  mdiClockOutline,
  mdiCloseCircle,
  mdiHelpCircleOutline,
  mdiImageOffOutline,
  mdiMinusCircleOutline,
  mdiPencilOutline
} from '@mdi/js'

import { CheckValidationStatus } from '@/Network/Types/Responses/CheckValidationResponses'
import { CHECK_VALIDATION_STATUS_LABELS } from '@/constants/check-validation.constants'

import type { CheckValidationStatusColor } from './types'

export interface StatusConfig {
  label: string
  color: CheckValidationStatusColor
  icon: string
}

export const STATUS_CONFIG: Record<CheckValidationStatus, StatusConfig> = {
  [CheckValidationStatus.PENDING_REVIEW]: {
    label: CHECK_VALIDATION_STATUS_LABELS.PENDING_REVIEW,
    color: 'yellow',
    icon: mdiClockOutline
  },
  [CheckValidationStatus.APPROVED]: {
    label: CHECK_VALIDATION_STATUS_LABELS.APPROVED,
    color: 'green',
    icon: mdiCheckCircle
  },
  [CheckValidationStatus.REJECTED]: {
    label: CHECK_VALIDATION_STATUS_LABELS.REJECTED,
    color: 'red',
    icon: mdiCloseCircle
  },
  [CheckValidationStatus.NOT_EVALUATED]: {
    label: CHECK_VALIDATION_STATUS_LABELS.NOT_EVALUATED,
    color: 'gray',
    icon: mdiMinusCircleOutline
  },
  [CheckValidationStatus.NOT_APPLICABLE]: {
    label: CHECK_VALIDATION_STATUS_LABELS.NOT_APPLICABLE,
    color: 'gray',
    icon: mdiMinusCircleOutline
  }
}

// Status novo do backend antes do FE acompanhar cai em badge neutro — nunca `undefined`.
export const UNKNOWN_STATUS_CONFIG: StatusConfig = {
  label: 'Desconhecido',
  color: 'gray',
  icon: mdiHelpCircleOutline
}

export const statusConfigFor = (status: CheckValidationStatus): StatusConfig =>
  STATUS_CONFIG[status] ?? UNKNOWN_STATUS_CONFIG

/**
 * Marcadores secundários da célula de status (o protótipo empilha até dois
 * badges): não são status, são qualificadores da linha.
 */
export const OVERDUE_BADGE: StatusConfig = {
  label: 'Atrasado',
  color: 'orange',
  icon: mdiClockAlertOutline
}

export const EDITED_SINCE_SUBMIT_BADGE: StatusConfig = {
  label: 'Alterado após o envio',
  color: 'blue',
  icon: mdiPencilOutline
}

export const EVIDENCE_UNAVAILABLE_BADGE: StatusConfig = {
  label: 'Evidência indisponível',
  color: 'gray',
  icon: mdiImageOffOutline
}

// Espelha o ColumnConfig do TableModular. Duplicado de propósito: importar o type
// nomeado de um .vue dentro de um .ts quebra o build (ts-loader só vê o default).
interface ColumnConfig {
  key: string
  label: string
  width?: string
  align?: 'left' | 'center'
}

// Cada key tem um slot `column-{key}` no CheckValidationTable. As colunas
// numéricas curtas (Dif., Evid.) centralizam cabeçalho e célula no mesmo eixo.
export const COLUMNS: ColumnConfig[] = [
  { key: 'registro', label: 'Plantão', width: '110px', align: 'left' },
  { key: 'medico', label: 'Médico', width: 'minmax(190px, 1.3fr)', align: 'left' },
  { key: 'unidade', label: 'Unidade e Escala', width: 'minmax(180px, 1.2fr)', align: 'left' },
  { key: 'data', label: 'Data do plantão', width: '130px', align: 'left' },
  { key: 'previsto', label: 'Previsto', width: '120px', align: 'center' },
  { key: 'digitado', label: 'Digitado', width: '120px', align: 'center' },
  { key: 'dif', label: 'Dif.', width: '90px', align: 'center' },
  { key: 'status', label: 'Status', width: '200px', align: 'center' }
]

// Nome da unidade na célula: a coluna é elástica, então o corte por CSS varia com
// a tela. O limite fixo mantém a linha previsível; o tooltip traz o nome completo.
export const UNIT_NAME_MAX_LENGTH = 18

export const EMPTY_STATE_TITLE = 'Nenhum registro aguardando validação'
